// ─── Explore Trails Query Hook ──────────────────────────────────────
// Replaces the imperative loadNearbyTrails / loadForYouTrails / etc.
// pattern in the Explore screen with a single useQuery that switches
// endpoints based on category, location, and filters.

import { useInfiniteQuery, useQuery, keepPreviousData } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import {
  fetchNearbyTrails,
  fetchForYouTrails,
  fetchTrailsByTag,
  searchTrailsAPI,
  countTrailsAPI,
  type TrailPage,
} from '@/lib/api/trails';
import type { Trail } from '@/types/trail';
import type { ExploreFilters } from '@/hooks/useExploreFilters';
import { filtersToParams } from '@/hooks/useExploreFilters';

interface UseExploreTrailsOptions {
  token: string | undefined;
  selectedCategory: string;
  locationCoords: { latitude: number; longitude: number } | undefined;
  filters: ExploreFilters;
  hasActiveFilters: boolean;
  buildQueryParams: () => Record<string, string | string[]>;
  dogId?: string;
  /** Stale time in ms — default 1 min (location-dependent data) */
  staleTime?: number;
}

export function useExploreTrails({
  token,
  selectedCategory,
  locationCoords,
  filters,
  hasActiveFilters,
  buildQueryParams,
  dogId,
  staleTime = 60_000,
}: UseExploreTrailsOptions) {
  // Use the map-panned center if available, otherwise the searched location, otherwise the user's location.
  const mapCenter =
    filters.mapCenterLatitude != null && filters.mapCenterLongitude != null
      ? { latitude: filters.mapCenterLatitude, longitude: filters.mapCenterLongitude }
      : null;
  const searchCenter =
    filters.searchLatitude != null && filters.searchLongitude != null
      ? { latitude: filters.searchLatitude, longitude: filters.searchLongitude }
      : null;
  const userCenter = locationCoords;
  const queryCenter = mapCenter ?? searchCenter ?? userCenter;

  // Build a serialisable query key that changes whenever the fetch should re-run
  const queryParams = hasActiveFilters ? buildQueryParams() : {};
  const queryKey = [
    'explore',
    selectedCategory,
    queryCenter?.latitude,
    queryCenter?.longitude,
    userCenter?.latitude,
    userCenter?.longitude,
    filters.mapCenterLatitude,
    filters.mapCenterLongitude,
    filters.searchLatitude,
    filters.searchLongitude,
    hasActiveFilters ? JSON.stringify(queryParams) : null,
    dogId ?? null,
  ];

  return useInfiniteQuery<TrailPage, Error>({
    queryKey,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      if (!token) return { trails: [], nextOffset: null, total: 0 };

      const lat = queryCenter?.latitude;
      const lng = queryCenter?.longitude;
      const userLat = userCenter?.latitude;
      const userLng = userCenter?.longitude;
      const offset = pageParam as number;

      // If filters are active, use search endpoint
      if (hasActiveFilters) {
        if (typeof lat !== 'number' || typeof lng !== 'number') return { trails: [], nextOffset: null, total: 0 };
        return searchTrailsAPI(token, {
          latitude: lat,
          longitude: lng,
          filterParams: queryParams,
          distanceKm: 35,
          mapLatitude: filters.mapCenterLatitude ?? filters.searchLatitude ?? undefined,
          mapLongitude: filters.mapCenterLongitude ?? filters.searchLongitude ?? undefined,
          userLatitude: userLat,
          userLongitude: userLng,
          dogId,
          offset,
        });
      }

      // Category-based endpoints
      if (selectedCategory === 'for-you') {
        return fetchForYouTrails(token, {
          userLat, userLng,
          mapLat: lat, mapLng: lng,
          distanceKm: 35,
        }, dogId, offset);
      }

      if (selectedCategory === 'nearby') {
        return fetchNearbyTrails(token, {
          userLat, userLng,
          mapLat: lat, mapLng: lng,
          distanceKm: 35,
        }, dogId, offset);
      }

      // Tag-based categories
      return fetchTrailsByTag(token, selectedCategory, {
        userLat, userLng,
        mapLat: lat, mapLng: lng,
        distanceKm: 35,
      }, dogId, offset);
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    enabled: !!token,
    staleTime,
  });
}
// ─── Live Filter Count Query ────────────────────────────────────────
// Live "N trails" count for the filter sheet: hits /trail/search/count
// with the draft (not yet applied) filters so the count updates as the
// user toggles, before they hit apply.

interface UseTrailFilterCountOptions {
  token: string | undefined;
  /** Draft filters (the sheet's local state, not the applied ones). */
  filters: ExploreFilters;
  /** User's location — fallback center when no map pan / location search. */
  locationCoords: { latitude: number; longitude: number } | undefined;
  dogId?: string;
  /** Only fetch while the filter sheet is open. */
  enabled: boolean;
}

export function useTrailFilterCount({
  token,
  filters,
  locationCoords,
  dogId,
  enabled,
}: UseTrailFilterCountOptions) {
  const filterParams = filtersToParams(filters);
  const center = {
    latitude: filters.mapCenterLatitude ?? filters.searchLatitude ?? locationCoords?.latitude,
    longitude: filters.mapCenterLongitude ?? filters.searchLongitude ?? locationCoords?.longitude,
  };

  return useQuery<number, Error>({
    queryKey: [
      'trail-filter-count',
      center.latitude,
      center.longitude,
      locationCoords?.latitude,
      locationCoords?.longitude,
      JSON.stringify(filterParams),
      dogId ?? null,
    ],
    queryFn: async () => {
      if (!token || typeof center.latitude !== 'number' || typeof center.longitude !== 'number') return 0;
      return countTrailsAPI(token, {
        latitude: center.latitude,
        longitude: center.longitude,
        filterParams,
        distanceKm: 35,
        mapLatitude: filters.mapCenterLatitude ?? filters.searchLatitude ?? undefined,
        mapLongitude: filters.mapCenterLongitude ?? filters.searchLongitude ?? undefined,
        userLatitude: locationCoords?.latitude,
        userLongitude: locationCoords?.longitude,
        dogId,
      });
    },
    enabled: enabled && !!token,
    staleTime: 5_000,
    // keep the last count on screen while a new one is fetching (no flicker)
    placeholderData: keepPreviousData,
  });
}
